import type { World } from './types';

export const SNAPSHOT_INTERVAL = 50;
export const MAX_SNAPSHOTS = 400;

export interface SerializedAnt {
  x: number;
  y: number;
  carrying: boolean;
  regs: [number, number, number, number];
  pc: number;
  tag: number;
}

export interface WorldSnapshot {
  tick: number;
  foodCollected: number;
  rngState: number;
  cells: Uint8Array;
  food: Uint16Array;
  pheromones: Uint16Array;
  visitCounts: Uint32Array;
  ants: SerializedAnt[];
}

export function captureSnapshot(world: World): WorldSnapshot {
  return {
    tick: world.tick,
    foodCollected: world.foodCollected,
    rngState: world.rngState,
    cells: new Uint8Array(world.map.cells),
    food: new Uint16Array(world.map.food),
    pheromones: new Uint16Array(world.map.pheromones),
    visitCounts: new Uint32Array(world.map.visitCounts),
    ants: world.ants.map(a => ({
      x: a.x,
      y: a.y,
      carrying: a.carrying,
      regs: [...a.regs] as [number, number, number, number],
      pc: a.pc,
      tag: a.tag,
    })),
  };
}

export function restoreSnapshot(world: World, snapshot: WorldSnapshot): void {
  world.tick = snapshot.tick;
  world.foodCollected = snapshot.foodCollected;
  world.rngState = snapshot.rngState;
  world.map.cells.set(snapshot.cells);
  world.map.food.set(snapshot.food);
  world.map.pheromones.set(snapshot.pheromones);
  world.map.visitCounts.set(snapshot.visitCounts);
  for (let i = 0; i < snapshot.ants.length && i < world.ants.length; i++) {
    const sa = snapshot.ants[i];
    const a = world.ants[i];
    a.x = sa.x;
    a.y = sa.y;
    a.carrying = sa.carrying;
    a.regs = [...sa.regs] as [number, number, number, number];
    a.pc = sa.pc;
    a.tag = sa.tag ?? 0;
  }
}

export function findNearestSnapshot(
  snapshots: WorldSnapshot[],
  targetTick: number,
): WorldSnapshot | null {
  let best: WorldSnapshot | null = null;
  for (const snap of snapshots) {
    if (snap.tick <= targetTick) {
      if (!best || snap.tick > best.tick) best = snap;
    }
  }
  return best;
}
